import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  ChangeEvent,
  DragEvent,
} from "react";
import {
  AppBar,
  Typography,
  Button,
  Box,
  Breadcrumbs,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  Grid,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  SelectChangeEvent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { getDuplicateStatusMessage } from "./importStatusIcon.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PageDataContext } from "../../utils/common/PageDataContext.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";
import {
  finalizeImport,
  FinalizeRowPayload,
  normalizeYN,
  parseApiError,
  previewValidateImportRows,
  ValidateRow,
} from "../../services/itemImportStaging.ts";
import {
  clearMigrationSession,
  loadMigrationSession,
  PreviewSubView,
  saveMigrationSession,
} from "../../utils/items/dataMigrationSession.ts";
import {
  CON_MIGRATION_MAX_FILESIZE,
  CON_MIGRATION_MAX_FILESIZE_MB,
  CON_MIGRATION_FILE_MIME_TYPES,
  CON_MIGRATION_EXCEL_ACCEPT_TYPES,
  CON_MIGRATION_TEMPLATE_FILENAME,
  CON_MIGRATION_EXCEL_SHEET_NAME,
  CON_MIGRATION_EXCEL_COLUMN_WIDTH,
  CON_MIGRATION_DEFAULT_USER,
  CON_MIGRATION_DEFAULT_VARIANT_VALUE,
  CON_MIGRATION_SYSTEM_FIELDS,
  CON_MIGRATION_FILE_TYPE_TEMPLATE,
  CON_MIGRATION_FILE_TYPE_CUSTOM,
  CON_MIGRATION_DEFAULT_START_ROW,
  CON_MIGRATION_DEFAULT_START_COL,
  CON_MIGRATION_DEFAULT_START_COL_LETTER,
  CON_MIGRATION_DUPLICATE_SOURCE_BATCH,
  CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM,
} from "../../utils/sys/Constants.tsx";
import {
  getMsgFileSizeExceeded,
  MSG_TEMPLATE_DOWNLOADED,
  VALIDATION_MANDATORY_MAPPING,
  VALIDATION_TEMPLATE_MISMATCH,
  ERROR_PARSING_EXCEL,
  ERROR_MERGED_CELLS,
  ERROR_INVALID_START_POINT,
  ERROR_NO_DATA,
  MSG_IMPORT_COMPLETE,
  MSG_CONFIRM_CONTINUE_IMPORT,
  MSG_EXCLUDE_CONFIRM,
  MSG_NO_HEADER_WARNING,
  MSG_INVALID_FILE_FORMAT_XLS,
  MSG_NO_DATA_IN_FILE,
  MSG_DUPLICATE_IN_IMPORT_FILE,
  MSG_UNIT_PER_PACKAGE_MUST_BE_NUMBER,
  MSG_NO_ERROR_RECORDS,
  MSG_ERROR_REPORT_FAILED,
  MSG_SUBMISSION_FAILED,
  MSG_MANDATORY_FIELDS_MISSING,
  MSG_FIELD_ALREADY_MAPPED,
  MSG_RECORD_REMOVED_SUCCESS,
  MSG_AT_LEAST_ONE_ITEM_TYPE_YES,
} from "../../utils/sys/Messages.tsx";
import * as XLSX from "xlsx";
import { useStyles } from "../../assets/styles/Theme.tsx";

interface UserParam {
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
}

interface ExcelRow {
  _rowNumber: number;
  [key: string]: any;
}

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationErrorRow {
  row: number;
  item: string;
  errors: ValidationError[];
}

interface ValidationResults {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ValidationErrorRow[];
}

interface ImportResults {
  success: number;
  failed: number;
  unposted: number;
  invalid: number;
  duplicate: number;
  errors: Array<{ row: string; item: string; error: string }>;
}

interface FieldMapping {
  brandName: string;
  mainCategory: string;
  category: string;
  subCategory: string;
  genericName: string;
  itemName: string;
  uom: string;
  packagingType: string;
  modelName: string;
  unitPerPackage: string;
  description: string;
  isSelling: string;
  isInventory: string;
}

interface VariantFieldSlot {
  id: number;
  excelHeader: string;
}

interface VariantDataItem {
  variantcode: number;
  variantname: string;
  variantvalue: string;
}

interface ParsedExcelResult {
  headers: string[];
  dataRows: ExcelRow[];
}

interface TransformedRow {
  _id: number;
  _rowNumber: number;
  _recordStatus?: string;
  _duplicateSource?: "batch" | "system" | null;
  _variantData?: VariantDataItem[];
  _variantDisplay?: string;
  _clientErrors?: string[];
  [key: string]: any;
}

interface SystemField {
  key: string;
  label: string;
  mandatory: boolean;
}

function DataMigration(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const context = useContext(PageDataContext);
  const { setPageData } = context || {};
  const fileInputRef = useRef<HTMLInputElement>(null);
  const skipRebuildRef = useRef(false);
  const sessionRestoredRef = useRef(false);
  const lastParsedStartRef = useRef<{ row: number; col: number }>({
    row: CON_MIGRATION_DEFAULT_START_ROW,
    col: CON_MIGRATION_DEFAULT_START_COL,
  });

  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } =
    (SYS_USER.USER_PARAM as UserParam) || {};

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [previewSubView, setPreviewSubView] =
    useState<PreviewSubView>("summary");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [fileType, setFileType] = useState<string>(
    CON_MIGRATION_FILE_TYPE_CUSTOM
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [startRow, setStartRow] = useState<number>(
    CON_MIGRATION_DEFAULT_START_ROW
  );
  const [startRowInput, setStartRowInput] = useState<string>(
    String(CON_MIGRATION_DEFAULT_START_ROW)
  );
  const [startCol, setStartCol] = useState<number>(
    CON_MIGRATION_DEFAULT_START_COL
  );
  const [startColLetter, setStartColLetter] = useState<string>(
    CON_MIGRATION_DEFAULT_START_COL_LETTER
  );
  const [hasHeaders, setHasHeaders] = useState<boolean>(true);
  const [openNoHeaderDialog, setOpenNoHeaderDialog] = useState<boolean>(false);
  const [openTemplateInstructionsDialog, setOpenTemplateInstructionsDialog] =
    useState<boolean>(false);
  const [openConfirmContinueDialog, setOpenConfirmContinueDialog] =
    useState<boolean>(false);
  const [openExcludeConfirmDialog, setOpenExcludeConfirmDialog] =
    useState<boolean>(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const ITEM_TYPE_KEYS = new Set(["isSelling", "isInventory"]);

  const getRequiredFields = (): SystemField[] => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    return systemFields.filter(
      (f) => f.mandatory || ITEM_TYPE_KEYS.has(f.key)
    );
  };

  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({
    brandName: "",
    mainCategory: "",
    category: "",
    subCategory: "",
    genericName: "",
    itemName: "",
    uom: "",
    packagingType: "",
    modelName: "",
    unitPerPackage: "",
    description: "",
    isSelling: "",
    isInventory: "",
  });
  const [variantFieldMappings, setVariantFieldMappings] = useState<
    VariantFieldSlot[]
  >([{ id: 1, excelHeader: "" }]);
  const [variantFieldNextId, setVariantFieldNextId] = useState<number>(2);

  const [validationResults, setValidationResults] = useState<ValidationResults>(
    {
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      errors: [],
    }
  );
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [displayData, setDisplayData] = useState<TransformedRow[]>([]);
  const [dbValidationRows, setDbValidationRows] = useState<ValidateRow[]>([]);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [duplicateRowCount, setDuplicateRowCount] = useState<number>(0);
  const [allPreviewRows, setAllPreviewRows] = useState<
    Array<TransformedRow & { _recordStatus: string }>
  >([]);

  const [importResults, setImportResults] = useState<ImportResults>({
    success: 0,
    failed: 0,
    unposted: 0,
    invalid: 0,
    duplicate: 0,
    errors: [],
  });

  const [openRemoveSelectedDialog, setOpenRemoveSelectedDialog] =
    useState<boolean>(false);
  const [openRemoveSingleDialog, setOpenRemoveSingleDialog] =
    useState<boolean>(false);
  const [rowToRemove, setRowToRemove] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    if (setPageData) setPageData({ title: "Import Data" });
  }, [setPageData]);

  useEffect(() => {
    if (sessionRestoredRef.current) return;
    const session = loadMigrationSession();
    if (!session || session.currentStep !== 3) return;

    sessionRestoredRef.current = true;
    const restoredAllPreview = (session.allPreviewRows || []) as Array<
      TransformedRow & { _recordStatus: string }
    >;
    skipRebuildRef.current =
      restoredAllPreview.length > 0 ||
      (session.previewSubView === "preview" &&
        (session.displayData || []).length > 0);
    setFileType(session.fileType);
    setStartRow(session.startRow);
    setStartRowInput(session.startRowInput);
    setStartCol(session.startCol);
    setStartColLetter(session.startColLetter);
    setHasHeaders(session.hasHeaders);
    setExcelHeaders(session.excelHeaders);
    setExcelData(session.excelData as ExcelRow[]);
    setFieldMapping({
      brandName: "",
      mainCategory: "",
      category: "",
      subCategory: "",
      genericName: "",
      itemName: "",
      uom: "",
      packagingType: "",
      modelName: "",
      unitPerPackage: "",
      description: "",
      isSelling: "",
      isInventory: "",
      ...session.fieldMapping,
    });
    setVariantFieldMappings(session.variantFieldMappings);
    setDbValidationRows(session.dbValidationRows || []);
    setValidationResults(session.validationResults);
    setDuplicateRowCount(session.duplicateRowCount);
    setDisplayData(session.displayData as TransformedRow[]);
    setSelectedRows(new Set(session.selectedRows));
    setAllPreviewRows(restoredAllPreview);
    setPreviewSubView(session.previewSubView || "preview");
    setCurrentStep(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertColumnToNumber = (col: string | number): number => {
    if (typeof col === "number") return col;
    if (!col || typeof col !== "string") return 1;
    let result = 0;
    for (let i = 0; i < col.length; i++) {
      result = result * 26 + (col.charCodeAt(i) - 64);
    }
    return result;
  };

  const convertNumberToColumn = (num: number): string => {
    let result = "";
    let n = num;
    while (n > 0) {
      n--;
      result = String.fromCharCode(65 + (n % 26)) + result;
      n = Math.floor(n / 26);
    }
    return result || "A";
  };

  const downloadTemplate = (): void => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const templateHeaders = [
      ...systemFields.map((field) => field.label),
      "Variant",
    ];

    const ynFieldInstruction =
      "Mandatory. Enter Yes or No only. At least one of Selling Item or Inventory Item must be Yes.";
    const optionalFieldKeys = new Set(["modelName", "description"]);

    const instructionRow = [
      ...systemFields.map((field) => {
        if (field.key === "isSelling" || field.key === "isInventory") {
          return ynFieldInstruction;
        }
        if (optionalFieldKeys.has(field.key)) {
          return "Optional";
        }
        return "Mandatory";
      }),
      "Optional",
    ];

    const ws = XLSX.utils.aoa_to_sheet([templateHeaders, instructionRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, CON_MIGRATION_EXCEL_SHEET_NAME);
    ws["!cols"] = templateHeaders.map((header) => ({
      wch:
        header === "Selling Item" || header === "Inventory Item"
          ? 55
          : CON_MIGRATION_EXCEL_COLUMN_WIDTH,
    }));
    XLSX.writeFile(wb, CON_MIGRATION_TEMPLATE_FILENAME);
    setAlertType("success");
    setAlertMessage(MSG_TEMPLATE_DOWNLOADED);
    setAlertOpen(true);
  };

  const isAllowedFile = (file: File): boolean => {
    const name = file.name.toLowerCase();
    const byExt =
      name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".csv");
    const byMime =
      !file.type || CON_MIGRATION_FILE_MIME_TYPES.includes(file.type);
    return byExt || byMime;
  };

  const buildNoHeaderLabels = (firstRow: any[], col: number): string[] => {
    const labels: string[] = [];
    const usedCounts = new Map<string, number>();
    for (let i = col - 1; i < (firstRow?.length ?? 0); i++) {
      const colLetter = convertNumberToColumn(i + 1);
      const cellVal = String(firstRow[i] ?? "").trim();
      const base = cellVal || colLetter;
      const count = usedCounts.get(base) ?? 0;
      usedCounts.set(base, count + 1);
      labels.push(count === 0 ? base : `${base} (${colLetter})`);
    }
    return labels;
  };

  const isTemplateInstructionRow = (
    rowData: any[] | undefined,
    headerCount: number,
    col: number
  ): boolean => {
    if (!rowData || headerCount <= 0) return false;
    let hasContent = false;
    for (let idx = 0; idx < headerCount; idx++) {
      const cell = String(rowData[col - 1 + idx] ?? "").trim();
      if (!cell) continue;
      hasContent = true;
      const lower = cell.toLowerCase();
      const isInstruction =
        lower === "mandatory" ||
        lower === "optional" ||
        lower.startsWith("mandatory") ||
        lower.startsWith("optional") ||
        lower.includes("yes/no") ||
        lower.includes("yes or no");
      if (!isInstruction) return false;
    }
    return hasContent;
  };

  const parseExcelFromStartPoint = (
    file: File,
    row: number,
    col: number,
    withHeaders: boolean
  ): Promise<ParsedExcelResult> => {
    return new Promise((resolve, reject) => {
      setIsProcessing(true);
      setParsingError(null);
      setExcelHeaders([]);
      setExcelData([]);

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          if (worksheet["!merges"] && worksheet["!merges"].length > 0) {
            reject(new Error(ERROR_MERGED_CELLS));
            return;
          }

          const jsonData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
            header: 1,
            defval: "",
            raw: false,
          });

          if (jsonData.length < row) {
            reject(new Error(ERROR_INVALID_START_POINT));
            return;
          }

          let headers: string[] = [];
          let dataStartIndex: number;

          if (withHeaders) {
            const headerRow = jsonData[row - 1];
            if (!headerRow || headerRow.length === 0) {
              reject(new Error(ERROR_NO_DATA));
              return;
            }
            for (let i = col - 1; i < headerRow.length; i++) {
              const header = String(headerRow[i] || "").trim();
              if (header) headers.push(header);
              else break;
            }
            if (headers.length === 0) {
              reject(new Error(ERROR_NO_DATA));
              return;
            }
            dataStartIndex = row;
            if (dataStartIndex < jsonData.length) {
              const candidate = jsonData[dataStartIndex];
              if (isTemplateInstructionRow(candidate, headers.length, col)) {
                dataStartIndex += 1;
              }
            }
          } else {
            const firstDataRow = jsonData[row - 1];
            if (!firstDataRow || firstDataRow.length === 0) {
              reject(new Error(ERROR_NO_DATA));
              return;
            }
            headers = buildNoHeaderLabels(firstDataRow, col);
            if (headers.length === 0) {
              reject(new Error(ERROR_NO_DATA));
              return;
            }
            dataStartIndex = row - 1;
          }

          const dataRows: ExcelRow[] = [];
          for (let i = dataStartIndex; i < jsonData.length; i++) {
            const rowData = jsonData[i];
            if (
              !rowData ||
              rowData.every((cell) => !cell || String(cell).trim() === "")
            ) {
              continue;
            }
            const rowObj: ExcelRow = { _rowNumber: i + 1 };
            headers.forEach((header, idx) => {
              const cellIndex = col - 1 + idx;
              rowObj[header] = rowData[cellIndex]
                ? String(rowData[cellIndex]).trim()
                : "";
            });
            dataRows.push(rowObj);
          }

          if (dataRows.length === 0) {
            reject(new Error(MSG_NO_DATA_IN_FILE));
            return;
          }

          setExcelHeaders(headers);
          setExcelData(dataRows);
          resolve({ headers, dataRows });
        } catch (error) {
          console.error("Error parsing Excel:", error);
          reject(error);
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        setIsProcessing(false);
        reject(new Error(ERROR_PARSING_EXCEL));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const validateTemplate = (
    headers: string[]
  ): { isValid: boolean; missingHeaders: string[] } => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const expectedHeaders = systemFields.map((field) => field.label);
    const missingHeaders = expectedHeaders.filter(
      (expected) =>
        !headers.some((h) => h.toLowerCase() === expected.toLowerCase())
    );
    return { isValid: missingHeaders.length === 0, missingHeaders };
  };

  const autoMapFields = (headers: string[]): void => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const newMapping: Partial<FieldMapping> = {};
    systemFields.forEach((field) => {
      const matchingHeader = headers.find(
        (h) => h.toLowerCase() === field.label.toLowerCase()
      );
      if (matchingHeader) (newMapping as any)[field.key] = matchingHeader;
    });
    setFieldMapping((prev) => ({ ...prev, ...newMapping }));
  };

  const resetImportMappingState = (): void => {
    setFieldMapping({
      brandName: "",
      mainCategory: "",
      category: "",
      subCategory: "",
      genericName: "",
      itemName: "",
      uom: "",
      packagingType: "",
      modelName: "",
      unitPerPackage: "",
      description: "",
      isSelling: "",
      isInventory: "",
    });
    setVariantFieldMappings([{ id: 1, excelHeader: "" }]);
    setVariantFieldNextId(2);
    setDbValidationRows([]);
    setDuplicateRowCount(0);
    setAllPreviewRows([]);
    setDisplayData([]);
    setSelectedRows(new Set());
    setPreviewSubView("summary");
    clearMigrationSession();
  };

  const processParsedFile = async (
    file: File,
    withHeaders: boolean,
    showSuccess = true,
    overrides?: { startRow?: number; startCol?: number }
  ): Promise<boolean> => {
    try {
      const row = overrides?.startRow ?? startRow;
      const col = overrides?.startCol ?? startCol;
      const result = await parseExcelFromStartPoint(
        file,
        row,
        col,
        withHeaders
      );
      if (fileType === CON_MIGRATION_FILE_TYPE_TEMPLATE) {
        const validation = validateTemplate(result.headers);
        if (!validation.isValid) {
          setAlertType("error");
          setAlertMessage(VALIDATION_TEMPLATE_MISMATCH);
          setAlertOpen(true);
          setExcelHeaders([]);
          setExcelData([]);
          return false;
        }
        autoMapFields(result.headers);
      }
      lastParsedStartRef.current = { row, col };
      if (showSuccess) {
        setAlertType("success");
        setAlertMessage(
          `File parsed successfully! Found ${result.headers.length} columns and ${result.dataRows.length} rows.`
        );
        setAlertOpen(true);
        setTimeout(() => setAlertOpen(false), 2000);
      }
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : ERROR_PARSING_EXCEL;
      setAlertType("error");
      setAlertMessage(errorMessage);
      setAlertOpen(true);
      setParsingError(errorMessage);
      return false;
    }
  };

  const reparseUploadedFile = async (
    headerMode: boolean,
    showSuccess = false,
    overrides?: { startRow?: number; startCol?: number }
  ): Promise<void> => {
    if (!uploadedFile) return;
    resetImportMappingState();
    await processParsedFile(uploadedFile, headerMode, showSuccess, overrides);
  };

  const handleFileSelect = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isAllowedFile(file)) {
      setAlertType("error");
      setAlertMessage(MSG_INVALID_FILE_FORMAT_XLS);
      setAlertOpen(true);
      return;
    }
    if (file.size > CON_MIGRATION_MAX_FILESIZE) {
      setAlertType("error");
      setAlertMessage(getMsgFileSizeExceeded(CON_MIGRATION_MAX_FILESIZE_MB));
      setAlertOpen(true);
      return;
    }
    setUploadedFile(file);
    resetImportMappingState();
    const ok = await processParsedFile(file, hasHeaders, true);
    if (!ok) setUploadedFile(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const event = {
        target: { files: [file] },
      } as unknown as ChangeEvent<HTMLInputElement>;
      handleFileSelect(event);
      e.dataTransfer.clearData();
    }
  };

const getRowDuplicateKey = (row: ExcelRow): string | null => {
  const itemNameHeader = fieldMapping.itemName; // Only use Item Name as the base identifier
  
  if (!itemNameHeader) return null;
  const itemName = String(row[itemNameHeader] || "").trim();
  
  if (!itemName) return null;

  const variantSlots = variantFieldMappings.filter((s) => s.excelHeader);
  const variantPart = variantSlots
    .map((slot) =>
      String(row[slot.excelHeader] ?? "")
        .trim()
        .toLowerCase()
    )
    .join("|");

  // Format: itemName|variant1|variant2...
  return `${itemName.toLowerCase()}|${variantPart}`;
};

const getPreviewDuplicateKey = (row: TransformedRow): string | null => {
  const itemName = String(row.itemName || "").trim();
  
  if (!itemName) return null; // Only Item Name is strictly required for a duplicate key base

  const variantPart = (row._variantData || [])
    .map((v) => String(v.variantvalue ?? "").trim().toLowerCase())
    .join("|");

  // Format: itemName|variant1|variant2...
  return `${itemName.toLowerCase()}|${variantPart}`;
};

  const isPositiveInteger = (value: unknown): boolean => {
    const str = String(value ?? "").trim();
    return /^[1-9]\d*$/.test(str);
  };

  const formatVariantDisplay = (variantData?: VariantDataItem[]): string => {
    if (!variantData || !Array.isArray(variantData) || variantData.length === 0)
      return "None";
    const parts = variantData
      .filter((v) => v.variantname && String(v.variantvalue).trim() !== "")
      .map(
        (v) =>
          `${(v.variantname || "").trim()}: ${(v.variantvalue || "").trim()}`
      );
    return parts.length > 0 ? parts.join(" | ") : "None";
  };

  const getMappedVariantSlots = (): VariantFieldSlot[] =>
    variantFieldMappings.filter((s) => s.excelHeader);

  const validateAllRows = (): ValidationErrorRow[] => {
    if (excelData.length === 0) return [];
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const mandatoryFields = systemFields.filter((f) => f.mandatory);
    const errors: ValidationErrorRow[] = [];
    const seenInBatch = new Map<string, number>();
    let validCount = 0;
    let duplicateCount = 0;

    excelData.forEach((row, index) => {
      const rowErrors: ValidationError[] = [];

      mandatoryFields.forEach((field) => {
        if (field.key === "unitPerPackage") return;
        const mappedHeader = fieldMapping[field.key as keyof FieldMapping];
        if (!mappedHeader) return;
        const value = row[mappedHeader];
        if (!value || String(value).trim() === "") {
          rowErrors.push({
            field: field.label,
            message: MSG_MANDATORY_FIELDS_MISSING,
          });
        }
      });

      const uppHeader = fieldMapping.unitPerPackage;
      if (uppHeader) {
        const uppVal = row[uppHeader];
        if (!uppVal || String(uppVal).trim() === "") {
          rowErrors.push({
            field: CON_MIGRATION_SYSTEM_FIELDS.UNIT_PER_PACKAGE.label,
            message: MSG_MANDATORY_FIELDS_MISSING,
          });
        } else if (!isPositiveInteger(uppVal)) {
          rowErrors.push({
            field: CON_MIGRATION_SYSTEM_FIELDS.UNIT_PER_PACKAGE.label,
            message: MSG_UNIT_PER_PACKAGE_MUST_BE_NUMBER,
          });
        }
      }

      const itemTypeFields: Array<keyof FieldMapping> = [
        "isSelling",
        "isInventory",
      ];
      const ynValues: Record<string, string> = {};

      itemTypeFields.forEach((key) => {
        const mappedHeader = fieldMapping[key];
        if (!mappedHeader) return;
        const raw = String(row[mappedHeader] ?? "").trim().toLowerCase();
        ynValues[key as string] = raw;

        if (raw !== "" && raw !== "yes" && raw !== "no") {
          const fieldLabel =
            key === "isSelling"
              ? (CON_MIGRATION_SYSTEM_FIELDS as any).IS_SELLING?.label ||
                "Selling Item"
              : (CON_MIGRATION_SYSTEM_FIELDS as any).IS_INVENTORY?.label ||
                "Inventory Item";
          rowErrors.push({
            field: fieldLabel,
            message: "Selling/Inventory Item must be 'Yes' or 'No'.",
          });
        }
      });

      if (fieldMapping.isSelling && fieldMapping.isInventory) {
        const sellingRaw = ynValues.isSelling ?? "";
        const inventoryRaw = ynValues.isInventory ?? "";

        const sellingValidFormat =
          sellingRaw === "" || sellingRaw === "yes" || sellingRaw === "no";
        const inventoryValidFormat =
          inventoryRaw === "" || inventoryRaw === "yes" || inventoryRaw === "no";

        if (
          sellingValidFormat &&
          inventoryValidFormat &&
          sellingRaw !== "yes" &&
          inventoryRaw !== "yes"
        ) {
          rowErrors.push({
            field: "Item Type",
            message: MSG_AT_LEAST_ONE_ITEM_TYPE_YES,
          });
        }
      }

      const rowNumber = row._rowNumber || index + 1;
      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          item: row[fieldMapping.itemName] || `Row ${rowNumber}`,
          errors: rowErrors,
        });
        return;
      }

      const dupKey = getRowDuplicateKey(row);
      if (dupKey && seenInBatch.has(dupKey)) {
        duplicateCount++;
        errors.push({
          row: rowNumber,
          item: row[fieldMapping.itemName] || `Row ${rowNumber}`,
          errors: [
            { field: "Duplicate", message: MSG_DUPLICATE_IN_IMPORT_FILE },
          ],
        });
      } else {
        if (dupKey) seenInBatch.set(dupKey, rowNumber);
        validCount++;
      }
    });

    setDuplicateRowCount(duplicateCount);
    setValidationResults({
      totalRows: excelData.length,
      validRows: validCount,
      invalidRows: errors.length,
      errors,
    });
    return errors;
  };

  const buildClassifyPayload = () => {
    const rows = excelData.map((row) => {
      const getVal = (key: string) => {
        const mapped = fieldMapping[key as keyof FieldMapping];
        return mapped ? String(row[mapped] || "").trim() : "";
      };
      const variantSlots = variantFieldMappings.filter((s) => s.excelHeader);
      const variantdataArray = variantSlots.map((slot, idx) => ({
        variantcode: idx + 1,
        variantname: slot.excelHeader,
        variantvalue:
          row[slot.excelHeader] != null
            ? String(row[slot.excelHeader]).trim()
            : "",
      }));
      return {
        sourcerowno: row._rowNumber,
        brandname: getVal("brandName"),
        maincategory: getVal("mainCategory"),
        category: getVal("category"),
        subcategory: getVal("subCategory"),
        genericname: getVal("genericName"),
        itemname: getVal("itemName"),
        packagetype: getVal("packagingType"),
        measuringunit: getVal("uom"),
        modelname: getVal("modelName"),
        packageunits: getVal("unitPerPackage"),
        descrip: getVal("description"),
        isselling: normalizeYN(getVal("isSelling")),
        isinv: normalizeYN(getVal("isInventory")),
        variantdata:
          variantdataArray.length > 0
            ? JSON.stringify(variantdataArray)
            : JSON.stringify([
                {
                  variantcode: 0,
                  variantname: "",
                  variantvalue: CON_MIGRATION_DEFAULT_VARIANT_VALUE,
                },
              ]),
      };
    });
    return {
      compcode: COMPCODE || "",
      loccode: STAFFMAPLOCODE || "",
      enuser: USERNAME || CON_MIGRATION_DEFAULT_USER,
      filename: uploadedFile?.name || "",
      filetype: uploadedFile?.name?.split(".").pop() || "",
      rows,
    };
  };

  /**
   * ✅ FIXED: Build preview rows with variant-aware duplicate detection.
   *
   * Key rules:
   * - Server (system) duplicate status is HONORED for ALL rows, including the
   *   first occurrence. This ensures that when the same file is re-uploaded,
   *   every existing item is marked DUPLICATE (source: system) and routed to
   *   Unverified Items.
   * - Client (batch) duplicate detection only flags SUBSEQUENT occurrences
   *   within the file. The first occurrence is only VALID if the server does
   *   not flag it.
   * - Client-side non-duplicate validation errors always mark the row INVALID.
   */
  const buildAllPreviewRows = (
    clientErrors?: ValidationErrorRow[],
    dbRows?: ValidateRow[]
  ): Array<TransformedRow & { _recordStatus: string }> => {
    if (excelData.length === 0) return [];
    
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const errors = clientErrors ?? validationResults.errors;
    const dbValidationByRow = new Map(
      (dbRows ?? dbValidationRows).map((r) => [r.sourcerowno, r] as const)
    );

    type PreviewRowBuild = {
      transformedRow: TransformedRow;
      dbValidation?: ValidateRow;
      clientError?: ValidationErrorRow;
      dupKey: string | null;
    };

    const builtRows: PreviewRowBuild[] = excelData.map((row, index) => {
      const transformedRow: TransformedRow = {
        _id: index,
        _rowNumber: row._rowNumber || index + 1,
      };
      
      systemFields.forEach((field) => {
        const mappedHeader = fieldMapping[field.key as keyof FieldMapping];
        transformedRow[field.key] = mappedHeader ? row[mappedHeader] || "" : "";
      });
      
      const _variantData: VariantDataItem[] = variantFieldMappings
        .filter((slot) => slot.excelHeader)
        .map((slot, idx) => ({
          variantcode: idx + 1,
          variantname: slot.excelHeader,
          variantvalue:
            row[slot.excelHeader] != null
              ? String(row[slot.excelHeader]).trim()
              : "",
        }));
        
      transformedRow._variantData = _variantData;
      transformedRow._variantDisplay = formatVariantDisplay(_variantData);

      const clientError = errors.find(
        (e) => e.row === transformedRow._rowNumber
      );

      if (clientError) {
        const nonDupErrors = clientError.errors.filter(
          (e) => e.field !== "Duplicate"
        );
        if (nonDupErrors.length > 0) {
          transformedRow._clientErrors = nonDupErrors.map(
            (e) => `${e.field}: ${e.message}`
          );
        }
      }

      return {
        transformedRow,
        dbValidation: dbValidationByRow.get(transformedRow._rowNumber),
        clientError,
        dupKey: getPreviewDuplicateKey(transformedRow),
      };
    });

    // Track seen keys to identify batch duplicates (subsequent occurrences within file)
    const seenInBatch = new Map<string, number>();

    return builtRows.map(
      ({ transformedRow, dbValidation, clientError, dupKey }) => {
        // Client-side non-duplicate errors → INVALID
        const isClientInvalid = clientError?.errors.some(
          (e) => e.field !== "Duplicate"
        );

        // Is this a subsequent occurrence of a duplicate key within the batch?
        const isBatchDuplicateInFile = Boolean(
          dupKey && seenInBatch.has(dupKey)
        );

        // Server duplicate status
        const isSystemDuplicate = dbValidation?.recordstatus === "DUPLICATE";

        // Register the key (so subsequent rows know they're batch duplicates)
        if (dupKey && !seenInBatch.has(dupKey)) {
          seenInBatch.set(dupKey, transformedRow._rowNumber);
        }

        let recordStatus: string;
        let duplicateSource: "batch" | "system" | null = null;

        // Priority Order:
        if (isClientInvalid) {
          // 1. Client-side invalid (missing fields, etc.)
          recordStatus = "INVALID";
        } else if (isBatchDuplicateInFile) {
          // 2. Duplicate within the file (exact same item + variant combination)
          recordStatus = "DUPLICATE";
          duplicateSource = "batch";
        } else if (isSystemDuplicate) {
          // 3. System duplicate (Trust the backend completely)
          recordStatus = "DUPLICATE";
          duplicateSource = "system";
        } else if (dbValidation?.recordstatus === "INVALID") {
          // 4. Server-side invalid
          recordStatus = "INVALID";
        } else {
          // 5. Valid
          recordStatus = "VALID";
        }

        return {
          ...transformedRow,
          _recordStatus: recordStatus,
          _duplicateSource: duplicateSource,
        };
      }
    );
  };

  const recomputeValidationSummary = (
    previewRows: Array<TransformedRow & { _recordStatus: string }>
  ): void => {
    const validCount = previewRows.filter(
      (r) => r._recordStatus === "VALID"
    ).length;
    const invalidCount = previewRows.filter(
      (r) => r._recordStatus !== "VALID"
    ).length;
    const duplicateCount = previewRows.filter(
      (r) => r._recordStatus === "DUPLICATE"
    ).length;
    setDuplicateRowCount(duplicateCount);
    setValidationResults((prev) => ({
      ...prev,
      totalRows: excelData.length,
      validRows: validCount,
      invalidRows: invalidCount,
    }));
    setAllPreviewRows(previewRows);
  };

  const runPreviewValidate = async (): Promise<void> => {
    if (excelData.length === 0) return;
    setIsValidating(true);
    try {
      const payload = buildClassifyPayload();
      const result = await previewValidateImportRows(payload);
      const normalized = (result.rows || []).map((r: any) => ({
        sourcerowno: r.sourcerowno ?? r.SOURCEROWNO,
        recordstatus: r.recordstatus ?? r.RECORDSTATUS,
        validationerrors: r.validationerrors ?? r.VALIDATIONERRORS,
      }));
      setDbValidationRows(normalized);
      const clientErrors = validateAllRows();
      const preview = buildAllPreviewRows(clientErrors, normalized);
      recomputeValidationSummary(preview);
    } catch (error) {
      console.warn(
        "[Import] DB validation failed, falling back to client-side validation:",
        parseApiError(error)
      );
      setDbValidationRows([]);
      const clientErrors = validateAllRows();
      const preview = buildAllPreviewRows(clientErrors, []);
      recomputeValidationSummary(preview);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (
      currentStep === 3 &&
      excelData.length > 0 &&
      previewSubView === "summary"
    ) {
      if (skipRebuildRef.current) {
        skipRebuildRef.current = false;
        return;
      }
      void runPreviewValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, excelData, fieldMapping, previewSubView]);

  useEffect(() => {
    if (currentStep !== 3 || previewSubView !== "preview") return;
    if (skipRebuildRef.current) {
      skipRebuildRef.current = false;
      return;
    }
    if (allPreviewRows.length === 0) return;
    const valid = allPreviewRows.filter((r) => r._recordStatus === "VALID");
    setDisplayData(valid);
    setSelectedRows(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubView, allPreviewRows, currentStep]);

  const getPreviewRowErrors = (
    previewRow: TransformedRow & {
      _recordStatus?: string;
      _duplicateSource?: string | null;
    }
  ): string[] => {
    if (previewRow._recordStatus === "DUPLICATE") {
      const dbRow = dbValidationRows.find(
        (r) => r.sourcerowno === previewRow._rowNumber
      );
      const isClientDuplicate = validationResults.errors
        .find((e) => e.row === previewRow._rowNumber)
        ?.errors.some((e) => e.field === "Duplicate");

      if (previewRow._duplicateSource === "system") {
        return [getDuplicateStatusMessage("system")];
      }
      if (previewRow._duplicateSource === "batch") {
        return [getDuplicateStatusMessage("batch")];
      }
      if (dbRow?.recordstatus === "DUPLICATE") {
        return [getDuplicateStatusMessage("system")];
      }
      if (isClientDuplicate) {
        return [getDuplicateStatusMessage("batch")];
      }
      return [getDuplicateStatusMessage("batch")];
    }

    if (
      Array.isArray(previewRow._clientErrors) &&
      previewRow._clientErrors.length > 0
    ) {
      return previewRow._clientErrors;
    }

    const dbRow = dbValidationRows.find(
      (r) => r.sourcerowno === previewRow._rowNumber
    );
    if (dbRow?.recordstatus === "INVALID" && dbRow.validationerrors) {
      try {
        const parsed = JSON.parse(dbRow.validationerrors);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        return [dbRow.validationerrors];
      }
    }

    const errorRow = validationResults.errors.find(
      (e) => e.row === previewRow._rowNumber
    );
    if (!errorRow) return [];
    return errorRow.errors
      .filter((e) => e.field !== "Duplicate")
      .map((e) => `${e.field}: ${e.message}`);
  };

  const handleSelectAll = (): void => {
    setSelectedRows(new Set(displayData.map((_, idx) => idx)));
  };
  const handleDeselectAll = (): void => setSelectedRows(new Set());
  const handleRowToggle = (index: number): void => {
    const next = new Set(selectedRows);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelectedRows(next);
  };

  const confirmRemoveSelected = (): void => {
    const selectedIndices = Array.from(selectedRows);
    const newData = displayData.filter(
      (_, idx) => !selectedIndices.includes(idx)
    );
    setDisplayData(newData);
    setSelectedRows(new Set());
    setOpenRemoveSelectedDialog(false);
    setAlertType("success");
    setAlertMessage(MSG_RECORD_REMOVED_SUCCESS);
    setAlertOpen(true);
  };

  const confirmRemoveSingleRow = (): void => {
    if (rowToRemove === null) return;
    const newData = displayData.filter((_, idx) => idx !== rowToRemove);
    setDisplayData(newData);
    const newSelected = new Set(selectedRows);
    newSelected.delete(rowToRemove);
    const adjusted = new Set<number>();
    newSelected.forEach((idx) => {
      if (idx < rowToRemove) adjusted.add(idx);
      else if (idx > rowToRemove) adjusted.add(idx - 1);
    });
    setSelectedRows(adjusted);
    setRowToRemove(null);
    setOpenRemoveSingleDialog(false);
    setAlertType("success");
    setAlertMessage(MSG_RECORD_REMOVED_SUCCESS);
    setAlertOpen(true);
  };

  const handleDownloadPrepared = (): void => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const headers = [...systemFields.map((f) => f.label), "Variant"];
    const dataToExport = displayData.map((row) => [
      ...systemFields.map((field) => row[field.key] || ""),
      row._variantDisplay || "None",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataToExport]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prepared Data");
    XLSX.writeFile(wb, "Prepared_Import_Data.xlsx");
    setAlertType("success");
    setAlertMessage("Prepared data downloaded successfully!");
    setAlertOpen(true);
  };

  const handleDownloadErrors = (): void => {
    try {
      const systemFields = Object.values(
        CON_MIGRATION_SYSTEM_FIELDS
      ) as SystemField[];
      const variantSlots = getMappedVariantSlots();
      const variantHeaders = variantSlots.map(
        (_, i) => `Variant ${String(i + 1).padStart(2, "0")}`
      );
      const errorRows = allPreviewRows.filter(
        (r) => r._recordStatus === "INVALID" || r._recordStatus === "DUPLICATE"
      );
      if (errorRows.length === 0) {
        setAlertType("info");
        setAlertMessage(MSG_NO_ERROR_RECORDS);
        setAlertOpen(true);
        return;
      }
      const headers = [
        "Row Number",
        "Status",
        ...systemFields.map((f) => f.label),
        ...variantHeaders,
        "Error Details",
      ];
      const data = errorRows.map((row) => [
        row._rowNumber,
        row._recordStatus,
        ...systemFields.map((field) => row[field.key] || ""),
        ...variantSlots.map(
          (_, i) => row._variantData?.[i]?.variantvalue || ""
        ),
        getPreviewRowErrors(row).join("; "),
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Error Rows");
      XLSX.writeFile(wb, "Import_Error_Rows.xlsx");
      setAlertType("success");
      setAlertMessage(
        `Downloaded ${errorRows.length} error row(s) successfully!`
      );
      setAlertOpen(true);
    } catch {
      setAlertType("error");
      setAlertMessage(MSG_ERROR_REPORT_FAILED);
      setAlertOpen(true);
    }
  };

  const resolveDuplicateSourceFlag = (row: TransformedRow): string => {
    if (row._duplicateSource === "system")
      return CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM;
    if (row._duplicateSource === "batch")
      return CON_MIGRATION_DUPLICATE_SOURCE_BATCH;
    const dbRow = dbValidationRows.find(
      (d) => d.sourcerowno === row._rowNumber
    );
    if (dbRow?.recordstatus === "DUPLICATE")
      return CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM;
    return CON_MIGRATION_DUPLICATE_SOURCE_BATCH;
  };

  const toFinalizeRow = (
    row: TransformedRow,
    errors?: string,
    duplicatesource?: string
  ): FinalizeRowPayload => {
    const variantData = row._variantData;
    let variantdataJson = JSON.stringify([
      {
        variantcode: 0,
        variantname: "",
        variantvalue: CON_MIGRATION_DEFAULT_VARIANT_VALUE,
      },
    ]);
    if (variantData && Array.isArray(variantData) && variantData.length > 0) {
      const arr = variantData
        .filter(
          (v) =>
            (v.variantname || "").trim() !== "" ||
            (v.variantvalue || "").trim() !== ""
        )
        .map((v, index) => ({
          variantcode: index + 1,
          variantname: (v.variantname || "").trim(),
          variantvalue: (v.variantvalue || "").trim(),
        }));
      if (arr.length > 0) variantdataJson = JSON.stringify(arr);
    } else if (
      typeof row._variantDisplay === "string" &&
      row._variantDisplay.trim().startsWith("[")
    ) {
      variantdataJson = row._variantDisplay;
    }

    return {
      sourcerowno: row._rowNumber,
      brandname: String(row.brandName || ""),
      maincategory: String(row.mainCategory || ""),
      category: String(row.category || ""),
      subcategory: String(row.subCategory || ""),
      genericname: String(row.genericName || ""),
      itemname: String(row.itemName || ""),
      packagetype: String(row.packagingType || ""),
      measuringunit: String(row.uom || ""),
      modelname: String(row.modelName || ""),
      packageunits: String(row.unitPerPackage || ""),
      descrip: String(row.description || ""),
      isselling: normalizeYN(row.isSelling),
      isinv: normalizeYN(row.isInventory),
      variantdata: variantdataJson,
      validationerrors: errors,
      duplicatesource,
    };
  };

  /**
   * ✅ FIXED: executeFinalize now rebuilds the authoritative classification for
   * ALL rows and ensures that server-flagged duplicates (including first
   * occurrences) are always sent in duplicateRows so they get routed to
   * Unverified Items.
   */
  const executeFinalize = async (excludedIndices: number[]): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      setAlertType("error");
      setAlertMessage("Company or location context is missing.");
      setAlertOpen(true);
      return;
    }

    const dataToSubmit = displayData.filter(
      (_, idx) => !excludedIndices.includes(idx)
    );
    const excludedRows = displayData.filter((_, idx) =>
      excludedIndices.includes(idx)
    );

    // ✅ Rebuild fresh preview rows to get the authoritative status for ALL rows
    const freshPreviewRows = buildAllPreviewRows();

    const invalidRows = freshPreviewRows.filter(
      (r) => r._recordStatus === "INVALID"
    );
    const duplicateRows = freshPreviewRows.filter(
      (r) => r._recordStatus === "DUPLICATE"
    );

    // Map rowNumber → status so we can filter displayData down to only the
    // rows that are truly VALID according to the fresh classification.
    const statusByRowNumber = new Map<number, string>();
    freshPreviewRows.forEach((r) =>
      statusByRowNumber.set(r._rowNumber, r._recordStatus)
    );

    const trulyValidToSubmit = dataToSubmit.filter(
      (r) => statusByRowNumber.get(r._rowNumber) === "VALID"
    );

    if (
      trulyValidToSubmit.length === 0 &&
      displayData.length > 0 &&
      excludedIndices.length === displayData.length
    ) {
      // All valid rows excluded — still finalize
    } else if (
      trulyValidToSubmit.length === 0 &&
      invalidRows.length === 0 &&
      duplicateRows.length === 0 &&
      excludedRows.length === 0
    ) {
      setAlertType("error");
      setAlertMessage("No rows to process.");
      setAlertOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        compcode: COMPCODE,
        loccode: STAFFMAPLOCODE,
        enuser: USERNAME || CON_MIGRATION_DEFAULT_USER,
        filename: uploadedFile?.name || "",
        filetype: uploadedFile?.name?.split(".").pop() || "",
        validItems: trulyValidToSubmit.map((r) => toFinalizeRow(r)),
        excludedValidRows: excludedRows.map((r) => toFinalizeRow(r)),
        invalidRows: invalidRows.map((r) =>
          toFinalizeRow(r, JSON.stringify(getPreviewRowErrors(r)))
        ),
        duplicateRows: duplicateRows.map((r) => {
          const source = resolveDuplicateSourceFlag(r);
          return toFinalizeRow(
            r,
            JSON.stringify([getDuplicateStatusMessage(source)]),
            source
          );
        }),
      };

      const result = await finalizeImport(payload);
      const imported =
        (result as any).importedRows ??
        (result as any).ImportedRows ??
        trulyValidToSubmit.length;
      const unposted =
        (result as any).unpostedRows ??
        (result as any).UnpostedRows ??
        excludedRows.length;
      const invalid =
        (result as any).invalidRows ??
        (result as any).InvalidRows ??
        invalidRows.length;
      const duplicate =
        (result as any).duplicateRows ??
        (result as any).DuplicateRows ??
        duplicateRows.length;

      setImportResults({
        success: imported,
        failed: 0,
        unposted,
        invalid,
        duplicate,
        errors: [],
      });
      setAlertType("success");
      setAlertMessage(MSG_IMPORT_COMPLETE);
      setAlertOpen(true);
      clearMigrationSession();
      setCurrentStep(4);
    } catch (error: any) {
      const errorMessage = parseApiError(error) || MSG_SUBMISSION_FAILED;
      setImportResults({
        success: 0,
        failed: displayData.length,
        unposted: 0,
        invalid: 0,
        duplicate: 0,
        errors: [{ row: "All", item: "Import", error: errorMessage }],
      });
      setAlertType("error");
      setAlertMessage(`Import failed: ${errorMessage}`);
      setAlertOpen(true);
      setCurrentStep(4);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const excludedIndices = Array.from(selectedRows);
    if (excludedIndices.length > 0) {
      setOpenExcludeConfirmDialog(true);
      return;
    }
    await executeFinalize([]);
  };

  const persistMigrationSession = (): void => {
    saveMigrationSession({
      currentStep: 3,
      previewSubView: "preview",
      excelData,
      excelHeaders,
      fieldMapping,
      variantFieldMappings,
      dbValidationRows,
      validationResults,
      duplicateRowCount,
      selectedRows: Array.from(selectedRows),
      displayData,
      allPreviewRows,
      startRow,
      startCol,
      hasHeaders,
      fileType,
      uploadedFileName: uploadedFile?.name || "",
      startRowInput,
      startColLetter,
    });
  };

  const handleNavigateToUnverifiedItems = (): void => {
    persistMigrationSession();
    navigate("/unverified-items", { state: { fromDataMigration: true } });
  };

  const handleNext = async (): Promise<void> => {
    if (currentStep === 1) {
      if (!uploadedFile || excelHeaders.length === 0) {
        setAlertType("error");
        setAlertMessage("Please upload a file first.");
        setAlertOpen(true);
        return;
      }
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      const requiredFields = getRequiredFields();
      const allMapped = requiredFields.every(
        (field) => fieldMapping[field.key as keyof FieldMapping]
      );
      if (!allMapped) {
        setAlertType("error");
        setAlertMessage(VALIDATION_MANDATORY_MAPPING);
        setAlertOpen(true);
        return;
      }
      setPreviewSubView("summary");
      setCurrentStep(3);
      return;
    }
    if (currentStep === 3 && previewSubView === "summary") {
      if (isValidating) return;
      setOpenConfirmContinueDialog(true);
    }
  };

  const getValidPreviewRows = (): Array<
    TransformedRow & { _recordStatus: string }
  > => {
    if (allPreviewRows.length > 0) {
      return allPreviewRows.filter((r) => r._recordStatus === "VALID");
    }
    const source = buildAllPreviewRows();
    if (source.length > 0) {
      recomputeValidationSummary(source);
    }
    return source.filter((r) => r._recordStatus === "VALID");
  };

  const handleConfirmContinue = (): void => {
    setOpenConfirmContinueDialog(false);
    const valid = getValidPreviewRows();
    setDisplayData(valid);
    setSelectedRows(new Set());
    setPreviewSubView("preview");
  };

  const handleBack = (): void => {
    if (currentStep === 3 && previewSubView === "preview") {
      skipRebuildRef.current = true;
      setPreviewSubView("summary");
      return;
    }
    if (currentStep === 3) {
      setDbValidationRows([]);
      setDuplicateRowCount(0);
      setAllPreviewRows([]);
      setPreviewSubView("summary");
    }
    if (currentStep > 1 && currentStep !== 4) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStatusIconLocal = (
    status: string,
    duplicateSource: "batch" | "system" | null | undefined,
    _hasNoErrors: boolean
  ): React.ReactElement => {
    if (status === "VALID") {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    if (status === "DUPLICATE") {
      return (
        <InfoIcon
          color="info"
          fontSize="small"
          aria-label={
            duplicateSource === "system" ? "System duplicate" : "Batch duplicate"
          }
        />
      );
    }
    return <WarningIcon color="warning" fontSize="small" />;
  };

  const renderStep1 = (): React.ReactElement => (
    <Card sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">File Upload</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Template instructions">
            <IconButton
              aria-label="Template instructions"
              color="primary"
              onClick={() => setOpenTemplateInstructionsDialog(true)}
              size="small"
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          border: `2px dashed ${
            theme.palette.mode === "dark" ? "#444" : "#ccc"
          }`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.default,
          mb: 3,
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isProcessing ? (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Processing file...</Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon
              sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              {uploadedFile
                ? uploadedFile.name
                : "Upload your Excel file here or browse"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop files here or click to browse
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1 }}
              color="text.secondary"
            >
              {`Supported formats: .xls, .xlsx, .csv (Max ${CON_MIGRATION_MAX_FILESIZE_MB}MB)`}
            </Typography>
          </>
        )}
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        accept={CON_MIGRATION_EXCEL_ACCEPT_TYPES}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
      {parsingError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {parsingError}
        </Alert>
      )}
      {excelHeaders.length > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Data fetching successful.
        </Alert>
      )}

      <Box sx={{ mb: 3, display: "none" }}>
        <Typography variant="subtitle1" gutterBottom>
          My data has headers
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={hasHeaders ? "yes" : "no"}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const yes = e.target.value === "yes";
              if (!yes) setOpenNoHeaderDialog(true);
              else {
                setHasHeaders(true);
                void reparseUploadedFile(true);
              }
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {hasHeaders ? "Header Starting Point" : "Data Starting Point"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <TextField
              label="Start Row"
              type="number"
              value={startRowInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setStartRowInput(value);
                const numValue = parseInt(value);
                if (value !== "" && !isNaN(numValue) && numValue >= 1)
                  setStartRow(numValue);
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const value = e.target.value.trim();
                const numValue = parseInt(value);
                let nextRow = 1;
                if (value === "" || isNaN(numValue) || numValue < 1) {
                  setStartRowInput("1");
                  setStartRow(1);
                  nextRow = 1;
                } else {
                  setStartRowInput(String(numValue));
                  setStartRow(numValue);
                  nextRow = numValue;
                }
                if (
                  uploadedFile &&
                  nextRow !== lastParsedStartRef.current.row
                ) {
                  void reparseUploadedFile(hasHeaders, false, {
                    startRow: nextRow,
                  });
                }
              }}
              size="small"
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Start Column"
              value={startColLetter}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const letter = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z]/g, "");
                setStartColLetter(letter);
                setStartCol(convertColumnToNumber(letter));
              }}
              onBlur={() => {
                const letter =
                  startColLetter.trim() ||
                  CON_MIGRATION_DEFAULT_START_COL_LETTER;
                const nextCol = convertColumnToNumber(letter);
                if (!startColLetter.trim()) {
                  setStartColLetter(CON_MIGRATION_DEFAULT_START_COL_LETTER);
                  setStartCol(nextCol);
                }
                if (
                  uploadedFile &&
                  nextCol !== lastParsedStartRef.current.col
                ) {
                  void reparseUploadedFile(hasHeaders, false, {
                    startCol: nextCol,
                  });
                }
              }}
              size="small"
              placeholder="A"
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          The first row of the uploaded file has been detected as the header.
        </Alert>
      </Box>
    </Card>
  );

  const renderStep2 = (): React.ReactElement => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const itemTypeKeys = new Set(["isSelling", "isInventory"]);
    const mandatoryFields = systemFields.filter((f) => f.mandatory);
    const itemTypeFields = systemFields.filter((f) => itemTypeKeys.has(f.key));
    const optionalFields = systemFields.filter(
      (f) => !f.mandatory && !itemTypeKeys.has(f.key)
    );
    const handleMappingChange = (
      fieldKey: string,
      excelHeader: string
    ): void => {
      if (excelHeader && isHeaderAlreadyMapped(excelHeader, fieldKey)) {
        setAlertType("error");
        setAlertMessage(MSG_FIELD_ALREADY_MAPPED);
        setAlertOpen(true);
        return;
      }
      setFieldMapping((prev) => ({ ...prev, [fieldKey]: excelHeader }));
    };
    const isMappingValid = (): boolean =>
      mandatoryFields.every(
        (field) => fieldMapping[field.key as keyof FieldMapping]
      );

    const isHeaderAlreadyMapped = (
      header: string,
      excludeFieldKey?: string,
      excludeVariantSlotId?: number
    ): boolean => {
      if (!header) return false;
      const usedBySystemField = Object.entries(fieldMapping).some(
        ([key, mappedHeader]) =>
          key !== excludeFieldKey && mappedHeader === header
      );
      if (usedBySystemField) return true;
      return variantFieldMappings.some(
        (slot) =>
          slot.id !== excludeVariantSlotId && slot.excelHeader === header
      );
    };

    const handleVariantMappingChange = (
      slotId: number,
      excelHeader: string
    ): void => {
      if (
        excelHeader &&
        isHeaderAlreadyMapped(excelHeader, undefined, slotId)
      ) {
        setAlertType("error");
        setAlertMessage(MSG_FIELD_ALREADY_MAPPED);
        setAlertOpen(true);
        return;
      }
      setVariantFieldMappings((prev) =>
        prev.map((s) => (s.id === slotId ? { ...s, excelHeader } : s))
      );
    };

    const renderFieldMappingRow = (
      field: SystemField,
      required = false
    ): React.ReactElement => {
      const mappedHeader =
        fieldMapping[field.key as keyof FieldMapping] || "";
      return (
        <Grid item xs={12} sm={6} key={field.key}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                minWidth: 200,
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" component="span">
                {field.label}
                {required && (
                  <span style={{ color: "red", marginLeft: 2 }}>*</span>
                )}
              </Typography>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                value={mappedHeader}
                onChange={(e: SelectChangeEvent) =>
                  handleMappingChange(field.key, e.target.value)
                }
              >
                <MenuItem value="">
                  <em>Not Mapped</em>
                </MenuItem>
                {excelHeaders.map((header) => (
                  <MenuItem
                    key={header}
                    value={header}
                    disabled={
                      header !== mappedHeader &&
                      isHeaderAlreadyMapped(header, field.key)
                    }
                  >
                    {header}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      );
    };

    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Field Mapping
        </Typography>
        {excelHeaders.length === 0 ? (
          <Alert severity="warning">
            Please upload and parse an Excel file in Step 1 first.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {hasHeaders
                ? "Map Excel column headers to system fields. Mandatory fields must be mapped."
                : "Map columns to system fields. Column options show values from the first data row."}
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              Mandatory Fields
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {mandatoryFields.map((field) => renderFieldMappingRow(field, true))}
            </Grid>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              Optional Fields
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {optionalFields.map((field) => renderFieldMappingRow(field))}
            </Grid>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
              Item Type
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {itemTypeFields.map((field) => renderFieldMappingRow(field, true))}
            </Grid>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Variant Fields
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setVariantFieldMappings((prev) => [
                    ...prev,
                    { id: variantFieldNextId, excelHeader: "" },
                  ]);
                  setVariantFieldNextId((n) => n + 1);
                }}
              >
                Add Variant Field
              </Button>
            </Box>
            <Grid container spacing={2}>
              {variantFieldMappings.map((slot, index) => (
                <Grid item xs={12} sm={6} key={slot.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        minWidth: 200,
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="body2" component="span">
                        Variant {String(index + 1).padStart(2, "0")}
                      </Typography>
                    </Box>
                    <FormControl fullWidth size="small">
                      <Select
                        value={slot.excelHeader || ""}
                        onChange={(e: SelectChangeEvent) =>
                          handleVariantMappingChange(slot.id, e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Not Mapped</em>
                        </MenuItem>
                        {excelHeaders.map((header) => (
                          <MenuItem
                            key={header}
                            value={header}
                            disabled={
                              header !== (slot.excelHeader || "") &&
                              isHeaderAlreadyMapped(header, undefined, slot.id)
                            }
                          >
                            {header}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {!isMappingValid() && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {VALIDATION_MANDATORY_MAPPING}
              </Alert>
            )}
          </>
        )}
      </Card>
    );
  };

  const renderStep3Summary = (): React.ReactElement => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    const variantSlots = getMappedVariantSlots();
    const previewRows =
      allPreviewRows.length > 0
        ? allPreviewRows
        : buildAllPreviewRows(validationResults.errors, dbValidationRows);

    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Validation Summary
        </Typography>
        {isValidating && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Validating against database…
            </Typography>
          </Box>
        )}
        {validationResults.totalRows === 0 && !isValidating ? (
          <Alert severity="warning">
            No data to validate. Please complete previous steps.
          </Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: theme.palette.info.light + "20",
                  }}
                >
                  <Typography variant="h4" color="info.main">
                    {validationResults.totalRows}
                  </Typography>
                  <Typography variant="body2">Total Rows</Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: theme.palette.success.light + "20",
                  }}
                >
                  <Typography variant="h4" color="success.main">
                    {validationResults.validRows}
                  </Typography>
                  <Typography variant="body2">Valid Rows</Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: theme.palette.error.light + "20",
                  }}
                >
                  <Typography variant="h4" color="error.main">
                    {validationResults.invalidRows}
                  </Typography>
                  <Typography variant="body2">Invalid Rows</Typography>
                </Card>
              </Grid>
            </Grid>
            {excelData.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {duplicateRowCount > 0
                      ? `Duplicates detected: ${duplicateRowCount}. Excluded duplicates will be available under Unverified Items.`
                      : "Review all uploaded records before continuing."}
                  </Typography>
                  {(validationResults.invalidRows > 0 ||
                    duplicateRowCount > 0) && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadErrors}
                      color="error"
                    >
                      Download Error Report
                    </Button>
                  )}
                </Box>
                <TableContainer sx={{ maxHeight: 450 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Row</TableCell>
                        {systemFields.map((field) => (
                          <TableCell key={field.key}>{field.label}</TableCell>
                        ))}
                        {variantSlots.map((_, i) => (
                          <TableCell key={`variant-${i}`}>
                            Variant {String(i + 1).padStart(2, "0")}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewRows.map((row, idx) => {
                        const rowErrors = getPreviewRowErrors(row);
                        const hasErrors = rowErrors.length > 0;
                        const statusIcon = renderStatusIconLocal(
                          row._recordStatus,
                          row._duplicateSource,
                          !hasErrors
                        );
                        return (
                          <TableRow
                            key={idx}
                            sx={{
                              backgroundColor:
                                row._recordStatus === "INVALID"
                                  ? theme.palette.warning.light + "15"
                                  : row._recordStatus === "DUPLICATE"
                                  ? theme.palette.info.light + "15"
                                  : undefined,
                            }}
                          >
                            <TableCell>
                              {hasErrors ? (
                                <Tooltip
                                  title={rowErrors.join("; ")}
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      cursor: "help",
                                    }}
                                  >
                                    {statusIcon}
                                  </Box>
                                </Tooltip>
                              ) : (
                                statusIcon
                              )}
                            </TableCell>
                            <TableCell>{row._rowNumber}</TableCell>
                            {systemFields.map((field) => (
                              <TableCell
                                key={field.key}
                                sx={{
                                  maxWidth: 150,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={String(row[field.key] || "-")}
                              >
                                {row[field.key] || "-"}
                              </TableCell>
                            ))}
                            {variantSlots.map((_, i) => (
                              <TableCell
                                key={`variant-val-${i}`}
                                sx={{
                                  maxWidth: 150,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={String(
                                  row._variantData?.[i]?.variantvalue || "-"
                                )}
                              >
                                {row._variantData?.[i]?.variantvalue || "-"}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Card>
    );
  };

  const renderStep3Preview = (): React.ReactElement => {
    const systemFields = Object.values(
      CON_MIGRATION_SYSTEM_FIELDS
    ) as SystemField[];
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">Data Preview (Valid Records)</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button variant="outlined" size="small" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="outlined" size="small" onClick={handleDeselectAll}>
              Deselect All
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenRemoveSelectedDialog(true)}
              disabled={selectedRows.size === 0}
            >
              Delete
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPrepared}
            >
              Download Valid Data
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleNavigateToUnverifiedItems}
            >
              Unverified Items
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Checked rows will be excluded from import and moved to Unverified
          Items. Selected to exclude: {selectedRows.size} / {displayData.length}
        </Typography>
        {displayData.length === 0 ? (
          <Alert severity="info">No valid records to import.</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Exclude</TableCell>
                  {systemFields.map((field) => (
                    <TableCell key={field.key}>{field.label}</TableCell>
                  ))}
                  <TableCell>Variant</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.has(index)}
                        onChange={() => handleRowToggle(index)}
                      />
                    </TableCell>
                    {systemFields.map((field) => (
                      <TableCell
                        key={field.key}
                        sx={{
                          maxWidth: 150,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={String(row[field.key] || "-")}
                      >
                        {row[field.key] || "-"}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={String(row._variantDisplay || "None")}
                    >
                      {row._variantDisplay || "None"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove row from import">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setRowToRemove(index);
                            setOpenRemoveSingleDialog(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    );
  };

  const renderStep4 = (): React.ReactElement => (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Import Results
      </Typography>
      {importResults.success === 0 &&
      importResults.failed === 0 &&
      importResults.unposted === 0 &&
      importResults.invalid === 0 &&
      importResults.duplicate === 0 ? (
        <Alert severity="info">
          No import results yet. Please complete the previous steps and submit
          the data.
        </Alert>
      ) : (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Card
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.info.light + "20",
                }}
              >
                <Typography variant="h4" color="info.main">
                  {importResults.success +
                    importResults.unposted +
                    importResults.invalid +
                    importResults.duplicate}
                </Typography>
                <Typography variant="body2">Total</Typography>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.success.light + "20",
                }}
              >
                <Typography variant="h4" color="success.main">
                  {importResults.success}
                </Typography>
                <Typography variant="body2">Imported</Typography>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.warning.light + "20",
                }}
              >
                <Typography variant="h4" color="warning.main">
                  {importResults.unposted}
                </Typography>
                <Typography variant="body2">Unposted</Typography>
              </Card>
            </Grid>
            <Grid item xs={3}>
              <Card
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.error.light + "20",
                }}
              >
                <Typography variant="h4" color="error.main">
                  {importResults.invalid +
                    importResults.duplicate +
                    importResults.failed}
                </Typography>
                <Typography variant="body2">Excluded / Failed</Typography>
              </Card>
            </Grid>
          </Grid>
          {importResults.success > 0 && importResults.failed === 0 && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {MSG_IMPORT_COMPLETE}
            </Alert>
          )}
          <Button
            sx={{ mt: 2, textTransform: "none" }}
            variant="outlined"
            onClick={() => navigate("/unverified-items")}
          >
            View Unverified Items
          </Button>
          {importResults.errors.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Failed Items:
              </Typography>
              {importResults.errors.slice(0, 10).map((error, index) => (
                <Alert key={index} severity="error" sx={{ mb: 1 }}>
                  Row {error.row}: {error.item} - {error.error}
                </Alert>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Card>
  );

  const steps: string[] = [
    "File Upload",
    "Field Mapping",
    "Validation & Preview",
    "Import Results",
  ];
  const stepperActiveStep = Math.min(
    Math.max(currentStep - 1, 0),
    steps.length - 1
  );

  return (
    <>
      <Box sx={styles.box_add_page_container as any}>
        <SystemAlert
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          message={alertMessage}
          type={alertType}
        />

        <Box sx={styles.box_breadcrumb as any}>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Tooltip title="Back to Item List" arrow>
              <IconButton
                sx={styles.btn_breadcrumb_back as any}
                onClick={() => navigate("/items")}
              >
                <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon as any} />
              </IconButton>
            </Tooltip>
            <Typography sx={styles.breadcrumb_divider as any} ml={1} mr={1}>
              |
            </Typography>
            <Breadcrumbs separator="›" sx={styles.breadcrumb as any}>
              <Typography sx={styles.breadcrumb_link as any}>Home</Typography>
              <Typography sx={styles.breadcrumb_link as any}>
                Item Management
              </Typography>
              <Typography sx={styles.breadcrumb_link_page as any}>
                Import Data
              </Typography>
            </Breadcrumbs>
          </Box>
        </Box>

        <AppBar sx={styles.appbar as any}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography sx={styles.appbar_title as any}>Import Data</Typography>
          </Box>
        </AppBar>

        <Box sx={{ width: "100%", mb: 4, mt: 3 }}>
          <Stepper activeStep={stepperActiveStep} alternativeLabel>
            {steps.map((label, index) => {
              const isStep1Completed = index === 0 && excelHeaders.length > 0;
              const isImportResultsCompleted =
                index === steps.length - 1 &&
                importResults.success > 0 &&
                importResults.failed === 0;
              const isCompleted =
                isStep1Completed ||
                isImportResultsCompleted ||
                index < stepperActiveStep;
              return (
                <Step key={label} completed={isCompleted}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        <Box sx={styles.add_form_page_container as any}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 &&
            previewSubView === "summary" &&
            renderStep3Summary()}
          {currentStep === 3 &&
            previewSubView === "preview" &&
            renderStep3Preview()}
          {currentStep === 4 && renderStep4()}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            px: 3,
            pb: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={
              currentStep === 1 || currentStep === 4
                ? () => {
                    clearMigrationSession();
                    navigate("/items", { state: { sortByImport: true } });
                  }
                : handleBack
            }
            disabled={isLoading}
            sx={{
              ...(styles.btn_secondary as any),
              ...(styles.btn_add_cancel as any),
            }}
          >
            {currentStep === 1 || currentStep === 4 ? "Cancel" : "Back"}
          </Button>

          {currentStep < 3 && (
            <Button
              variant="contained"
              onClick={() => {
                void handleNext();
              }}
              disabled={isLoading}
              sx={{
                ...(styles.btn_primary as any),
                ...(styles.btn_add_confirm as any),
              }}
            >
              Next
            </Button>
          )}
          {currentStep === 3 && previewSubView === "summary" && (
            <Button
              variant="contained"
              onClick={() => {
                void handleNext();
              }}
              disabled={isLoading || isValidating}
              sx={{
                ...(styles.btn_primary as any),
                ...(styles.btn_add_confirm as any),
              }}
            >
              Next
            </Button>
          )}
          {currentStep === 3 && previewSubView === "preview" && (
            <Button
              variant="contained"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={
                isLoading ||
                (displayData.length === 0 && allPreviewRows.length === 0)
              }
              sx={{
                ...(styles.btn_primary as any),
                ...(styles.btn_add_confirm as any),
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Importing...
                </>
              ) : (
                "Import Data"
              )}
            </Button>
          )}
          {currentStep === 4 && (
            <Button
              variant="contained"
              onClick={() =>
                navigate("/items", { state: { sortByImport: true } })
              }
              sx={{
                ...(styles.btn_primary as any),
                ...(styles.btn_add_confirm as any),
              }}
            >
              Done
            </Button>
          )}
        </Box>

        <AppDialog
          open={openTemplateInstructionsDialog}
          onClose={() => setOpenTemplateInstructionsDialog(false)}
          titleId="template-instructions-dialog-title"
          title="Import Template Instructions"
          maxWidth="sm"
          fullWidth
          hideCancel
          confirmLabel="Close"
          confirmColor="primary"
          onConfirm={() => setOpenTemplateInstructionsDialog(false)}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
              >
                Required Fields
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 0.5,
                  "& li": { typography: "body2" },
                }}
              >
                <li>Brand Name</li>
                <li>Item Main Category</li>
                <li>Item Category</li>
                <li>Item Subcategory</li>
                <li>Generic Name</li>
                <li>Item Name</li>
                <li>Packaging Type</li>
                <li>Units per Package</li>
                <li>Selling Item</li>
                <li>Inventory Item</li>
                <li>Unit of Measure (UOM)</li>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
              >
                Optional Fields
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  "& li": { typography: "body2", mb: 0.5 },
                }}
              >
                <li>Model Name</li>
                <li>Description</li>
                <li>Variant</li>
              </Box>
            </Box>

            <Alert severity="info" sx={{ alignItems: "flex-start" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Selling Item &amp; Inventory Item
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  "& li": { typography: "body2", mb: 0.5 },
                }}
              >
                <li>
                  Enter only <strong>Yes</strong> or <strong>No</strong> for
                  these fields.
                </li>
                <li>
                  At least one of the two fields must be <strong>Yes</strong>.
                </li>
              </Box>
            </Alert>
          </Box>
        </AppDialog>

        <AppDialog
          open={openNoHeaderDialog}
          onClose={() => setOpenNoHeaderDialog(false)}
          titleId="no-header-dialog-title"
          title="No Headers Warning"
          message={MSG_NO_HEADER_WARNING}
          cancelLabel="Cancel"
          confirmLabel="OK"
          confirmColor="primary"
          onConfirm={() => {
            setHasHeaders(false);
            setOpenNoHeaderDialog(false);
            void reparseUploadedFile(false);
          }}
        />

        <AppDialog
          open={openConfirmContinueDialog}
          onClose={() => setOpenConfirmContinueDialog(false)}
          titleId="confirm-continue-dialog-title"
          title="Confirm Continue"
          message={MSG_CONFIRM_CONTINUE_IMPORT}
          cancelLabel="Cancel"
          confirmLabel="OK"
          confirmColor="primary"
          onConfirm={handleConfirmContinue}
        />

        <AppDialog
          open={openExcludeConfirmDialog}
          onClose={() => setOpenExcludeConfirmDialog(false)}
          titleId="exclude-confirm-dialog-title"
          title="Exclude Selected Records"
          message={MSG_EXCLUDE_CONFIRM}
          cancelLabel="Cancel"
          confirmLabel="Continue"
          confirmColor="primary"
          onConfirm={() => {
            setOpenExcludeConfirmDialog(false);
            void executeFinalize(Array.from(selectedRows));
          }}
        />

        <AppDialog
          open={openRemoveSelectedDialog}
          onClose={() => setOpenRemoveSelectedDialog(false)}
          titleId="remove-selected-dialog-title"
          title="Confirm Remove Selected"
          message={`Are you sure you want to remove ${selectedRows.size} selected row(s)? This action cannot be undone.`}
          cancelLabel="Cancel"
          confirmLabel="Remove"
          confirmColor="error"
          onConfirm={confirmRemoveSelected}
        />

        <AppDialog
          open={openRemoveSingleDialog}
          onClose={() => {
            setRowToRemove(null);
            setOpenRemoveSingleDialog(false);
          }}
          titleId="remove-single-dialog-title"
          title="Confirm Remove Row"
          message="Are you sure you want to remove this row? This action cannot be undone."
          cancelLabel="Cancel"
          confirmLabel="Remove"
          confirmColor="error"
          onCancel={() => {
            setRowToRemove(null);
            setOpenRemoveSingleDialog(false);
          }}
          onConfirm={confirmRemoveSingleRow}
        />
      </Box>
    </>
  );
}

export default DataMigration;